import { apiBaseUrl, userApiUrl } from './apiConfig';
import { getAuthToken } from './authProvider';

const MEDIA_API_URL = `${userApiUrl}/media/admin`;
const BASE_PATH = 'service';
const AUDIO_FOLDER = `${BASE_PATH}/audio`;
const SUPPORTED_AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'];
const PUBLIC_PREFIXES = ['service', 'foodimage'];
const CURRENT_BUCKET = 'zoonsk-prod-media';
const LEGACY_BUCKET = 'zoonsk';
const LEGACY_STORAGE_HOST = 's3.twcstorage.ru';

const getHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const mediaRequestError = async (response) => {
    let message = `Media storage request failed (${response.status})`;
    try {
        const payload = await response.json();
        if (payload?.detail) message = payload.detail;
    } catch (_error) {
        // The status is enough when an upstream proxy did not return JSON.
    }
    const error = new Error(message);
    error.status = response.status;
    return error;
};

const uploadMedia = async (file, key) => {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${MEDIA_API_URL}/upload?${new URLSearchParams({ key })}`, {
        method: 'POST',
        headers: getHeaders(),
        body: form,
    });
    if (!response.ok) throw await mediaRequestError(response);
    const payload = await response.json();
    return payload.url;
};

const deleteMedia = async (key) => {
    const response = await fetch(`${MEDIA_API_URL}?${new URLSearchParams({ key })}`, {
        method: 'DELETE',
        headers: getHeaders(),
    });
    if (!response.ok) throw await mediaRequestError(response);
};

const getAudioExtension = (file) => {
    const mimeToExt = {
        'audio/mpeg': 'mp3',
        'audio/mp3': 'mp3',
        'audio/wav': 'wav',
        'audio/x-wav': 'wav',
        'audio/wave': 'wav',
        'audio/ogg': 'ogg',
        'audio/mp4': 'm4a',
        'audio/x-m4a': 'm4a',
        'audio/aac': 'aac',
        'audio/flac': 'flac',
        'audio/x-flac': 'flac',
    };

    const mimeExt = mimeToExt[file.type];
    if (mimeExt) return mimeExt;

    const nameParts = file.name.split('.');
    const extension = nameParts.length > 1 ? nameParts.pop().toLowerCase() : '';
    if (SUPPORTED_AUDIO_EXTENSIONS.includes(extension)) return extension;

    throw new Error('Неподдерживаемый аудиоформат. Разрешены: mp3, wav, ogg, m4a, aac, flac');
};

const deleteAudioVariants = async (animalId) => {
    await Promise.all(
        SUPPORTED_AUDIO_EXTENSIONS.map((extension) => (
            deleteMedia(`${AUDIO_FOLDER}/id_${animalId}.${extension}`)
        )),
    );
};

export const extractMediaKey = (fileUrl) => {
    const url = new URL(fileUrl);
    const currentStorageHost = new URL(apiBaseUrl).host;
    let expectedBucket;
    if (url.host === currentStorageHost) {
        expectedBucket = CURRENT_BUCKET;
    } else if (url.host === LEGACY_STORAGE_HOST) {
        expectedBucket = LEGACY_BUCKET;
    } else {
        throw new Error('Media URL belongs to an unknown storage host');
    }

    const segments = url.pathname
        .split('/')
        .filter(Boolean)
        .map((segment) => decodeURIComponent(segment));
    if (segments[0] !== expectedBucket || !PUBLIC_PREFIXES.includes(segments[1])) {
        throw new Error('Media URL does not contain a public ZooNSK key');
    }
    return segments.slice(1).join('/');
};

export const uploadListIcon = (file, animalId) => (
    uploadMedia(file, `${BASE_PATH}/list_icons/id_${animalId}.png`)
);

export const uploadMapIcon = (file, animalId) => (
    uploadMedia(file, `${BASE_PATH}/list_icons_map/id_${animalId}.png`)
);

export const uploadGalleryImage = (file, animalId, index) => (
    uploadMedia(file, `${BASE_PATH}/main_animals/main_${index}_id_${animalId}.png`)
);

export const uploadAnimalAudio = async (file, animalId) => {
    const extension = getAudioExtension(file);
    await deleteAudioVariants(animalId);
    return uploadMedia(file, `${AUDIO_FOLDER}/id_${animalId}.${extension}`);
};

export const deleteAnimalAudio = (animalId) => deleteAudioVariants(animalId);

export const uploadRouteImage = (file, routeId) => (
    uploadMedia(file, `${BASE_PATH}/list_rout/id_${routeId}.png`)
);

const QUEST_FOLDER = `${BASE_PATH}/quest`;

export const uploadQuestTaskImage = (file, questId) => (
    uploadMedia(file, `${QUEST_FOLDER}/task_id_${questId}.png`)
);

export const deleteQuestTaskImage = (questId) => (
    deleteMedia(`${QUEST_FOLDER}/task_id_${questId}.png`)
);

export const uploadQuestRewardImage = (file, rewardId, index) => (
    uploadMedia(file, `${QUEST_FOLDER}/reward_${index}_id_${rewardId}.png`)
);

export const deleteFileFromS3 = (fileUrl) => deleteMedia(extractMediaKey(fileUrl));

export const deleteListIcon = (animalId) => (
    deleteMedia(`${BASE_PATH}/list_icons/id_${animalId}.png`)
);

export const deleteGalleryImage = (animalId, index) => (
    deleteMedia(`${BASE_PATH}/main_animals/main_${index}_id_${animalId}.png`)
);

const safeOriginalName = (file) => file.name.replace(/[^a-zA-Zа-яА-Я0-9._-]/g, '_');

export const uploadNewsImage = (file, prefix) => (
    uploadMedia(file, `${BASE_PATH}/news/${prefix}_${Date.now()}_${safeOriginalName(file)}`)
);

export const uploadNewsVideo = (file, prefix) => (
    uploadMedia(file, `${BASE_PATH}/news/${prefix}_${Date.now()}_${safeOriginalName(file)}`)
);

export const deleteNewsMedia = (fileName) => deleteMedia(`${BASE_PATH}/news/${fileName}`);

export const uploadEventImage = (file, prefix) => (
    uploadMedia(file, `${BASE_PATH}/events/${prefix}_${Date.now()}_${safeOriginalName(file)}`)
);

export const deleteEventMedia = (fileName) => deleteMedia(`${BASE_PATH}/events/${fileName}`);
